import type {ReactElement} from 'react';
import React, {useEffect, useState, useRef} from 'react';
import type {DragDirection, Range, ResizeEndEvent, Span} from '@Libraries/timeline/types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {AudioWaveform} from '@Components/audio-waveform';
import {BaseTimelineItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-item/base-timeline-item';
import {TimelineItemDurationContainerType} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/duration-notification/duration-notification.types';
import {TimelineItemType} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-item/base-timeline-item.types';
import {updateDesignItemSelectedId, updateDesignItemSelectionState} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {FadeDirection} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/fade-background/fade-background.types';
import {AudioItemFadeBackground} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/audio-item-fade-background/audio-item-fade-background';
import {AudioItemFadeSlider} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/audio-item-fade-slider/audio-item-fade-slider';
import {onOpenStockAudioModal, onOpenUserAudioModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openAudioTypeModal} from '@Modals/audio-type-modal';
import {useDndMonitor} from '@dnd-kit/core';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import styles from './timeline-audio-item.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

interface ItemProps {
  id: string;
  range: Range;
  isLastItem?: boolean;
}

export function TimelineAudioItem({isLastItem = false, ...props}: ItemProps): ReactElement<any> | null {
  const audioItemData = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.audioItemsHashMap[props.id];
  });
  const selectedAudioItemUID = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID;
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [rangeWhileResizing, setRangeWhileResizing] = useState(props.range);
  const [dragDirectionWhileResizing, setDragDirectionWhileResizing] = useState<DragDirection>();
  const dispatch = useAppDispatch();
  const disabled = useRef(false);
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const isPanning = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isPanning;
  });
  const [rangeWhileDragging, setRangeWhileDragging] = useState(props.range);
  const [isDragging, setIsDragging] = useState(false);

  useEffect((): void => {
    setRangeWhileDragging(props.range);
  }, [props.range]);

  useDndMonitor({
    onDragStart(e) {
      if (e.active.id === props.id) {
        setIsDragging(true);
      }
    },
    onDragMove(e) {
      const dragElement = e.active.data.current;
      const updatedRange = dragElement?.getSpanFromDragEvent?.(e) as Range;
      if (updatedRange) {
        setRangeWhileDragging(updatedRange);
      }
    },
    onDragEnd(e) {
      if (e.active.id === props.id) {
        setIsDragging(false);
      }
    },
  });

  if (!audioItemData) {
    return null;
  }

  const updateStartTime = async (startTime: number, onPosterStartTime: number, undoable = false): Promise<void> => {
    if (!audioItemData) {
      return;
    }
    const newDuration = audioItemData.audioPlayer.trim.endTime - onPosterStartTime;
    const isTrimmed = newDuration !== audioItemData.audioPlayer.originalDuration;

    setIsUpdating(true);
    await window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap[props.id].updateFromObject(
      {
        audioPlayer: {
          trim: {
            isTrimmed,
            startTime: Number(startTime.toFixed(2)),
          },
        },
        onPosterStartTime: Number(onPosterStartTime.toFixed(2)),
      },
      {
        undoable,
      }
    );
    setIsUpdating(false);
  };

  const updateEndTime = async (endTime: number, undoable = false): Promise<void> => {
    if (!audioItemData) {
      return;
    }

    const newDuration = endTime - audioItemData.audioPlayer.trim.startTime;
    const isTrimmed = newDuration !== audioItemData.audioPlayer.originalDuration;
    setIsUpdating(true);
    await window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap[props.id].updateFromObject(
      {
        audioPlayer: {
          trim: {
            isTrimmed,
            endTime: Number(endTime.toFixed(2)),
          },
        },
      },
      {
        undoable,
      }
    );
    setIsUpdating(false);
  };

  const updateOnResize = async (range: Range, direction: DragDirection, undoable = false): Promise<void> => {
    const posterDuration = window.posterEditor?.whiteboard?.getDuration();
    if (!audioItemData || !posterDuration) {
      return;
    }

    const startingRange = range.start < 0 ? 0 : range.start;
    const endingRange = range.end > posterDuration ? posterDuration : range.end;
    const newDuration = (endingRange - startingRange) * audioItemData.audioPlayer.speed;
    const startTimeOnPoster = startingRange;
    const endTimeOnPoster = endingRange;

    if (direction === 'start') {
      if (newDuration > audioItemData.audioPlayer.originalDuration) {
        await updateStartTime(0, startTimeOnPoster, undoable);
        return;
      }

      if (newDuration < 1) {
        const newStartTime = audioItemData.audioPlayer.trim.endTime - 1;
        await updateStartTime(newStartTime, endTimeOnPoster - 1, undoable);
        return;
      }

      const difference = startTimeOnPoster - audioItemData.onPosterStartTime;
      const newStartTime = audioItemData.audioPlayer.trim.startTime + difference * audioItemData.audioPlayer.speed;
      await updateStartTime(newStartTime, startTimeOnPoster, undoable);
      return;
    }

    if (direction === 'end') {
      if (newDuration > audioItemData.audioPlayer.originalDuration) {
        await updateEndTime(audioItemData.audioPlayer.originalDuration, undoable);
        return;
      }

      if (newDuration < 1) {
        await updateEndTime(audioItemData.audioPlayer.trim.startTime + 1, undoable);
        return;
      }

      const newEndTime = (endTimeOnPoster - audioItemData.onPosterStartTime) * audioItemData.audioPlayer.speed + audioItemData.audioPlayer.trim.startTime;
      await updateEndTime(newEndTime, undoable);
    }
  };

  const validateUpdatedRange = (updatedRange: Span, direction: DragDirection): boolean => {
    if (direction === 'start' && updatedRange.start < 0) {
      return false;
    }

    const previousAudioStartTime = audioItemData.audioPlayer.trim.startTime;
    const previousAudioEndTime = audioItemData.audioPlayer.trim.endTime;
    const previousOnPosterStartTime = audioItemData.onPosterStartTime;
    const previousOnPosterEndTime = previousOnPosterStartTime + (previousAudioEndTime - previousAudioStartTime);

    if (direction === 'start') {
      const difference = updatedRange.start - previousOnPosterStartTime;
      const currentDuration = updatedRange.end - updatedRange.start;

      return previousAudioStartTime + difference >= 0 && currentDuration >= 1 / audioItemData.audioPlayer.speed;
    }

    const difference = updatedRange.end - previousOnPosterEndTime;
    const currentDuration = updatedRange.end - updatedRange.start;

    return (
      previousAudioEndTime + difference <= audioItemData.audioPlayer.originalDuration / audioItemData.audioPlayer.speed && currentDuration >= 1 / audioItemData.audioPlayer.speed
    );
  };

  const onResizeMove = (updatedRange: Span, direction: DragDirection): void => {
    const newDuration = (updatedRange.end - updatedRange.start) * audioItemData.audioPlayer.speed;
    if (audioItemData && audioItemData.audioPlayer.originalDuration < newDuration) {
      return;
    }

    if (!validateUpdatedRange(updatedRange, direction)) {
      return;
    }

    setDragDirectionWhileResizing(direction);
    setRangeWhileResizing(updatedRange);
  };

  const onResizeEnd = async (event: ResizeEndEvent): Promise<void> => {
    trackPosterBuilderGA4Events(GA4EventName.TRIM_AUDIO_TIMELINE, {
      [GA4EventParamName.TYPE]: isEditorMobileVariant() ? 'mobile' : 'web',
    });
    const updatedRange = event.active.data.current.getSpanFromResizeEvent?.(event);
    if (!updatedRange || !audioItemData) {
      return;
    }

    await updateOnResize(updatedRange, event.direction, true);
    setTimeout((): void => {
      setIsResizing(false);
    }, 200);
  };

  const getAudioText = (): string => {
    const audioHashmap = window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap;
    if (audioHashmap && audioHashmap[props.id]) {
      return audioHashmap[props.id].name;
    }
    return '';
  };

  const getTimeInSeconds = (): number => {
    if (!audioItemData) {
      return 0;
    }
    return (audioItemData.audioPlayer.trim.endTime - audioItemData.audioPlayer.trim.startTime) / audioItemData.audioPlayer.speed;
  };

  const getFadeInBackground = (): ReactElement<any> | null => {
    return <AudioItemFadeBackground widthPerSecond={widthPerSecond} audioId={props.id} direction={FadeDirection.LEFT} />;
  };

  const getFadeOutBackground = (): ReactElement<any> | null => {
    return <AudioItemFadeBackground widthPerSecond={widthPerSecond} audioId={props.id} direction={FadeDirection.RIGHT} />;
  };

  const isFadeApplied = (): boolean => {
    return !!window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap[props.id]?.audioPlayer?.hasFade();
  };

  const getLeftForAudioWaveform = (): string | undefined => {
    if (!audioItemData) {
      return undefined;
    }
    const existingLeft = -audioItemData.audioPlayer.trim.startTime * widthPerSecond;

    if (isResizing || isUpdating) {
      if (dragDirectionWhileResizing === 'end') {
        return `${existingLeft}px`;
      }

      const differenceInStartTimeOnPoster = rangeWhileResizing.start - props.range.start;
      const leftToAdd = -differenceInStartTimeOnPoster * widthPerSecond;
      return `${existingLeft + leftToAdd}px`;
    }

    return `${existingLeft}px`;
  };

  const getAudioItemContent = (): ReactElement<any> => {
    return (
      <>
        {getFadeInBackground()}
        {getFadeOutBackground()}
        <div
          className={`${styles.contentContainer} _overflow-hidden ${isDragging ? styles.isDragging : ''} ${isFadeApplied() ? styles.hideBg : ''} _full-height flex-row-justify-center flex-items-center`}
        >
          <Icon icon="icon-audio" size={IconSize.SIZE_ICON_12} className={`spacing-m-r-2 ${styles.icon}`} />
          <Text className="-truncated-text" val={getAudioText()} size={TextSize.XXSMALL} />
        </div>
        {audioItemData && !isPanning ? (
          <>
            <AudioWaveform
              style={{
                background: 'transparent',
                top: 0.5,
                left: getLeftForAudioWaveform(),
                width: `${(audioItemData.audioPlayer.originalDuration * widthPerSecond) / audioItemData.audioPlayer.speed}px`,
              }}
              doShowLoader={false}
              additionalClasses={`${styles.waveform} ${isDragging ? styles.isDragging : ''}`}
              waveSurferProps={{
                backgroundColor: 'transparent',
                waveColor: '#CABBF3',
                barWidth: 1.5,
                barRadius: 1.5,
                barMinHeight: 1,
                barGap: 3,
                height: 24,
                audioRate: audioItemData.audioPlayer.speed,
              }}
              id={`audiowaveform-${props.id}`}
              key={`audiowaveform-${props.id}-${widthPerSecond}-${audioItemData.audioPlayer.speed}`}
              url={audioItemData.audioPlayer.audioUrl}
              isPlaying={false}
              endTime={audioItemData.audioPlayer.originalDuration}
              seek={0}
              duration={audioItemData.audioPlayer.originalDuration}
            />
            <div className={`${styles.waveformDummyContainer} _full-width`} />
          </>
        ) : null}
        {selectedAudioItemUID === props.id && !isEditorMobileVariant() ? (
          <>
            <div
              onPointerDown={(): void => {
                disabled.current = true;
              }}
              onPointerUp={(): void => {
                disabled.current = false;
              }}
              className={`${styles.fadeInSliderWrapper} ${isResizing ? '_hidden' : ''}`}
            >
              <AudioItemFadeSlider fadeDirection={FadeDirection.LEFT} audioItemId={props.id} />
            </div>
            <div
              onPointerDown={(): void => {
                disabled.current = true;
              }}
              onPointerUp={(): void => {
                disabled.current = false;
              }}
              className={`${styles.fadeOutSliderWrapper} ${isResizing ? '_hidden' : ''}`}
            >
              <AudioItemFadeSlider fadeDirection={FadeDirection.RIGHT} audioItemId={props.id} />
            </div>
          </>
        ) : null}
      </>
    );
  };

  const onClickHandler = (): void => {
    if (!audioItemData?.uid) {
      return;
    }
    if (selectedAudioItemUID !== audioItemData.uid) {
      const newSelectionId = audioItemData.uid;
      dispatch(updateDesignItemSelectionState(false));
      dispatch(updateDesignItemSelectedId(''));
      window.posterEditor?.whiteboard?.audioClips.selectAudioItem(newSelectionId);
    }
  };

  const onAddAudio = (): void => {
    openAudioTypeModal({
      onOpenUserAudioModal,
      onOpenStockAudioModal,
    });
  };

  const onAutoScroll = (increment: number, direction: DragDirection = 'end'): void => {
    if (direction === 'start') {
      setRangeWhileResizing((oldRange) => {
        return {
          start: oldRange.start + increment,
          end: oldRange.end,
        };
      });
      return;
    }
    setRangeWhileResizing((oldRange) => {
      return {
        start: oldRange.start,
        end: oldRange.end + increment,
      };
    });
  };

  const getRange = (): Range => {
    if (isDragging) {
      return rangeWhileDragging;
    }
    return isResizing || isUpdating ? rangeWhileResizing : props.range;
  };

  return (
    <BaseTimelineItem
      onResizeMove={onResizeMove}
      onResizeEnd={(e): void => {
        void onResizeEnd(e);
      }}
      onResizeStart={(e): void => {
        setDragDirectionWhileResizing(e.direction);
        setRangeWhileResizing(props.range);
        setIsResizing(true);
      }}
      id={props.id}
      key={props.id}
      range={getRange()}
      duration={getTimeInSeconds()}
      durationItemType={TimelineItemDurationContainerType.Purple}
      type={TimelineItemType.Purple}
      isSelected={audioItemData && selectedAudioItemUID === audioItemData.uid}
      onClick={onClickHandler}
      disabled={disabled.current}
      showAddButton={isLastItem}
      onClickAddButton={onAddAudio}
      maxDuration={audioItemData.audioPlayer.originalDuration / audioItemData.audioPlayer.speed}
      minDuration={1 / audioItemData.audioPlayer.speed}
      isUpdating={isUpdating}
      onAutoScroll={onAutoScroll}
      posterDuration={window.posterEditor?.whiteboard?.getCurrentPage().getDuration()}
    >
      {getAudioItemContent()}
    </BaseTimelineItem>
  );
}
